import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { assetAPI, companyAPI, settingsAPI } from '../../lib/api';
import { createImage } from '../../lib/imageUtils';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { DataTable, SortableHeader } from '../../components/ui/data-table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import ModuleDisabled from '../../components/shared/ModuleDisabled';
import {
    Loader2,
    Plus,
    Monitor,
    Keyboard,
    Smartphone,
    Wifi,
    Package,
    Search,
    Download,
} from 'lucide-react';

const ASSET_CATEGORIES = [
    { value: 'HARDWARE', label: 'Hardware', icon: Monitor },
    { value: 'PERIPHERAL', label: 'Peripheral', icon: Keyboard },
    { value: 'SOFTWARE', label: 'Software', icon: Package },
    { value: 'MOBILE', label: 'Mobile', icon: Smartphone },
    { value: 'NETWORK', label: 'Network', icon: Wifi },
    { value: 'OTHER', label: 'Other', icon: Package },
];

// Map icon names to components
const ICON_MAP = { Monitor, Keyboard, Package, Smartphone, Wifi };

const REQUEST_PRIORITIES = [
    { value: 'LOW', label: 'Low' },
    { value: 'NORMAL', label: 'Normal' },
    { value: 'HIGH', label: 'High' },
    { value: 'URGENT', label: 'Urgent' },
];

function MyAssets() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [company, setCompany] = useState(null);
    const [myAssets, setMyAssets] = useState([]);
    const [myRequests, setMyRequests] = useState([]);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [requestData, setRequestData] = useState({
        category: '',
        description: '',
        priority: 'NORMAL',
        justification: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState('assets');
    const [assetCategories, setAssetCategories] = useState(ASSET_CATEGORIES);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showRequestDetailModal, setShowRequestDetailModal] = useState(false);

    const fetchMyAssets = useCallback(async () => {
        try {
            if (!user?.employee_id) return;
            const response = await assetAPI.getEmployeeAssets(user.employee_id);
            setMyAssets(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching my assets:', error);
            setMyAssets([]);
        }
    }, [user?.employee_id]);

    const fetchMyRequests = async () => {
        try {
            const response = await assetAPI.getRequests({});
            setMyRequests(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching my requests:', error);
            setMyRequests([]);
        }
    };

    const fetchCompany = async () => {
        try {
            const response = await companyAPI.getCurrent();
            setCompany(response.data);
        } catch (error) {
            console.error('Error fetching company:', error);
        }
    };

    const fetchAssetCategories = async () => {
        try {
            const response = await settingsAPI.getAssetCategories();
            if (Array.isArray(response.data) && response.data.length > 0) {
                const validCats = response.data.filter(cat => typeof cat.name === 'string');
                if (validCats.length > 0) {
                    const dynamicCats = validCats.map(cat => ({
                        value: cat.name.toUpperCase().replace(/\s+/g, '_'),
                        label: cat.name,
                        icon: ICON_MAP[cat.icon] || Package,
                    }));
                    setAssetCategories(dynamicCats);
                }
            }
        } catch (error) {
            console.log('Using default asset categories');
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchMyAssets(), fetchMyRequests(), fetchCompany(), fetchAssetCategories()]);
            setLoading(false);
        };
        loadData();
    }, [fetchMyAssets]);

    // Check module access
    if (company && !company.modules?.assets) {
        return <ModuleDisabled moduleName="IT Asset Management" />;
    }

    const handleSubmitRequest = async (e) => {
        e.preventDefault();
        if (!requestData.category || !requestData.description) {
            toast.error('Please fill in required fields');
            return;
        }

        setSubmitting(true);
        try {
            await assetAPI.createRequest(requestData);
            toast.success('Asset request submitted successfully');
            setShowRequestModal(false);
            setRequestData({ category: '', description: '', priority: 'NORMAL', justification: '' });
            fetchMyRequests();
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (Array.isArray(detail)) {
                toast.error(detail.map(e => e.msg).join(', '));
            } else if (typeof detail === 'string') {
                toast.error(detail);
            } else {
                toast.error('Failed to submit request');
            }
        } finally {
            setSubmitting(false);
        }
    };
    const downloadRequestPDF = async (request) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        let currentY = 20;
        
        // --- Header / Logo ---
        if (company?.logo) {
            try {
                const logoUrl = `${process.env.REACT_APP_BACKEND_URL}${company.logo}`;
                const img = await createImage(logoUrl);
                const mw = 40;
                const mh = 20;
                let w = img.width;
                let h = img.height;
                const ratio = w / h;
                if (w > mw) { w = mw; h = mw / ratio; }
                if (h > mh) { h = mh; w = mh * ratio; }
                doc.addImage(img, 'PNG', margin, currentY, w, h);
                currentY += h + 10;
            } catch (err) {
                console.error("Failed to load logo for PDF", err);
                currentY += 10;
            }
        } else {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.text(company?.name || 'Company Name', margin, currentY);
            currentY += 10;
        }

        // --- Date ---
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(80);
        const requestDate = new Date(request.created_at).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'long', year: 'numeric'
        });
        doc.text(`Date: ${requestDate}`, pageWidth - margin, 20, { align: 'right' });

        // --- To Address ---
        doc.setTextColor(0);
        doc.setFont('helvetica', 'bold');
        doc.text('To,', margin, currentY + 10);
        doc.setFont('helvetica', 'normal');
        doc.text('The IT Administration / Procurement Department', margin, currentY + 15);
        doc.text(company?.name || 'The Organization', margin, currentY + 20);
        currentY += 35;

        // --- Subject Line ---
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(`Subject: REQUISITION FOR ${request.category?.toUpperCase()} ASSET - ${request.priority?.toUpperCase()} PRIORITY`, margin, currentY);
        doc.line(margin, currentY + 1.5, pageWidth - margin, currentY + 1.5);
        currentY += 15;

        // --- Salutation & Body ---
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text('Dear Team,', margin, currentY);
        currentY += 8;
        const introText = `I am writing to formally submit a request for a new IT asset. Below are the specific details regarding the requirement for my professional duties:`;
        const splitIntro = doc.splitTextToSize(introText, pageWidth - (margin * 2));
        doc.text(splitIntro, margin, currentY);
        currentY += (splitIntro.length * 5) + 5;

        // --- Request Details ---
        const details = [
            ['Requester Name', user.name || '-'],
            ['Asset Category', getCategoryLabel(request.category)],
            ['Priority Level', request.priority || 'NORMAL'],
            ['Current Status', request.status || 'PENDING'],
            ['Submitted On', new Date(request.created_at).toLocaleString()]
        ];
        autoTable(doc, {
            startY: currentY,
            body: details,
            theme: 'plain',
            styles: { fontSize: 10, cellPadding: 2 },
            columnStyles: {
                0: { fontStyle: 'bold', textColor: [100, 100, 100], cellWidth: 40 },
                1: { textColor: [0, 0, 0] }
            },
            margin: { left: margin }
        });
        currentY = doc.lastAutoTable.finalY + 10;

        // --- Description ---
        doc.setFont('helvetica', 'bold');
        doc.text('Requirement Description:', margin, currentY);
        doc.setFont('helvetica', 'normal');
        const descText = request.description || 'No specific description provided.';
        const splitDesc = doc.splitTextToSize(descText, pageWidth - (margin * 2));
        doc.text(splitDesc, margin, currentY + 6);
        currentY += (splitDesc.length * 5) + 12;

        // --- Justification ---
        if (request.justification) {
            doc.setFont('helvetica', 'bold');
            doc.text('Justification for Request:', margin, currentY);
            doc.setFont('helvetica', 'normal');
            const splitJust = doc.splitTextToSize(request.justification, pageWidth - (margin * 2));
            doc.text(splitJust, margin, currentY + 6);
            currentY += (splitJust.length * 5) + 12;
        }

        // --- Closing ---
        const closingText = `I request you to kindly review this application and provide the necessary approval for the procurement/assignment of the mentioned asset.`;
        const splitClosing = doc.splitTextToSize(closingText, pageWidth - (margin * 2));
        doc.text(splitClosing, margin, currentY);
        currentY += (splitClosing.length * 5) + 15;
        doc.text('Sincerely,', margin, currentY);
        doc.setFont('helvetica', 'bold');
        doc.text(user.name || 'The Requester', margin, currentY + 6);
        doc.setFont('helvetica', 'normal');
        doc.text('Employee', margin, currentY + 11);

        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text('Note: This is a system-generated document and serves as a formal digital record of the request.', pageWidth / 2, doc.internal.pageSize.getHeight() - margin, { align: 'center' });
        doc.save(`Asset_Request_${request.id}.pdf`);
    };

    const getCategoryLabel = (category) => {
        const cat = assetCategories.find(c => c.value === category);
        return cat ? cat.label : (typeof category === 'string' ? category : 'Other');
    };

    const getCategoryIcon = (category) => {
        const cat = assetCategories.find(c => c.value === category);
        return cat ? cat.icon : Package;
    };

    // Asset columns for DataTable
    const assetColumns = [
        {
            accessorKey: 'asset_tag',
            header: ({ column }) => <SortableHeader column={column} title="Asset Tag" />,
            cell: ({ row }) => (
                <span className="font-mono font-medium text-primary">{row.original.asset_tag}</span>
            ),
        },
        {
            accessorKey: 'name',
            header: ({ column }) => <SortableHeader column={column} title="Name" />,
            cell: ({ row }) => {
                const Icon = getCategoryIcon(row.original.category);
                return (
                    <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{row.original.name}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: 'category',
            header: 'Category',
            cell: ({ row }) => (
                <Badge variant="secondary">{getCategoryLabel(row.original.category)}</Badge>
            ),
        },
        {
            accessorKey: 'brand',
            header: 'Brand/Model',
            cell: ({ row }) => {
                const { brand, model } = row.original;
                if (brand && model) return `${brand} ${model}`;
                return brand || model || '-';
            },
        },
        {
            accessorKey: 'serial_number',
            header: 'Serial Number',
            cell: ({ row }) => (
                <span className="font-mono text-sm">{row.original.serial_number || '-'}</span>
            ),
        },
        {
            accessorKey: 'condition',
            header: 'Condition',
            cell: ({ row }) => {
                const condition = row.original.condition;
                const conditionColors = {
                    EXCELLENT: 'text-green-600 border-green-600',
                    GOOD: 'text-blue-600 border-blue-600',
                    FAIR: 'text-yellow-600 border-yellow-600',
                    POOR: 'text-orange-600 border-orange-600',
                    DAMAGED: 'text-red-600 border-red-600',
                };
                return (
                    <Badge variant="outline" className={conditionColors[condition] || ''}>
                        {typeof condition === 'string' ? condition : 'N/A'}
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'assigned_at',
            header: 'Assigned On',
            cell: ({ row }) =>
                row.original.assigned_at
                    ? new Date(row.original.assigned_at).toLocaleDateString()
                    : '-',
        },
    ];

    // Request columns for DataTable
    const requestColumns = [
        {
            accessorKey: 'category',
            header: 'Category',
            cell: ({ row }) => (
                <Badge variant="secondary">{getCategoryLabel(row.original.category)}</Badge>
            ),
        },
        {
            accessorKey: 'description',
            header: 'Description',
            cell: ({ row }) => (
                <span className="max-w-xs truncate block">
                    {typeof row.original.description === 'string' ? row.original.description : '-'}
                </span>
            ),
        },
        {
            accessorKey: 'priority',
            header: 'Priority',
            cell: ({ row }) => {
                const priority = row.original.priority;
                const priorityColors = {
                    LOW: 'text-gray-600 border-gray-600',
                    NORMAL: 'text-blue-600 border-blue-600',
                    HIGH: 'text-orange-600 border-orange-600',
                    URGENT: 'text-red-600 border-red-600',
                };
                return (
                    <Badge variant="outline" className={priorityColors[priority] || ''}>
                        {typeof priority === 'string' ? priority : 'NORMAL'}
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const status = row.original.status;
                const statusColors = {
                    PENDING: 'bg-yellow-500',
                    APPROVED: 'bg-green-500',
                    REJECTED: 'bg-red-500',
                    FULFILLED: 'bg-blue-500',
                };
                return (
                    <Badge variant="outline" className="flex items-center gap-1 w-fit">
                        <span className={`w-2 h-2 rounded-full ${statusColors[status] || 'bg-gray-400'}`}></span>
                        {typeof status === 'string' ? status : 'PENDING'}
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'created_at',
            header: 'Requested On',
            cell: ({ row }) =>
                row.original.created_at
                    ? new Date(row.original.created_at).toLocaleDateString()
                    : '-',
        },
        {
            accessorKey: 'admin_remarks',
            header: 'Admin Response',
            cell: ({ row }) => (
                <span className="max-w-xs truncate block">
                    {row.original.admin_remarks || '-'}
                </span>
            ),
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <Button variant="ghost" size="sm" onClick={() => { setSelectedRequest(row.original); setShowRequestDetailModal(true); }}>
                    <Search className="h-4 w-4" />
                </Button>
            ),
        },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-slide-up">
            {/* Header */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">My Assets</h1>
                    <p className="text-muted-foreground">View your assigned assets and request new ones</p>
                </div>
                <Button onClick={() => setShowRequestModal(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Request Asset
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-3">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Assigned Assets</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">{myAssets.length}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Pending Requests</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold text-yellow-600">
                            {myRequests.filter(r => r.status === 'PENDING').length}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Approved Requests</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold text-green-600">
                            {myRequests.filter(r => r.status === 'APPROVED' || r.status === 'FULFILLED').length}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="assets">My Assets ({myAssets.length})</TabsTrigger>
                    <TabsTrigger value="requests">My Requests ({myRequests.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="assets" className="mt-4">
                    <DataTable
                        columns={assetColumns}
                        data={myAssets}
                        loading={false}
                        emptyMessage="No assets assigned to you yet"
                    />
                </TabsContent>

                <TabsContent value="requests" className="mt-4">
                    <DataTable
                        columns={requestColumns}
                        data={myRequests}
                        loading={false}
                        onRowClick={(req) => { setSelectedRequest(req); setShowRequestDetailModal(true); }}
                        emptyMessage="No asset requests found"
                    />
                </TabsContent>
            </Tabs>

            {/* Request Modal */}
            <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Request New Asset</DialogTitle>
                        <DialogDescription>
                            Submit a request for a new IT asset
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmitRequest}>
                        <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                                <Label>Asset Category *</Label>
                                <Select
                                    value={requestData.category}
                                    onValueChange={(value) => setRequestData({ ...requestData, category: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {assetCategories.map((cat) => (
                                            <SelectItem key={cat.value} value={cat.value}>
                                                {cat.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Description *</Label>
                                <Textarea
                                    value={requestData.description}
                                    onChange={(e) => setRequestData({ ...requestData, description: e.target.value })}
                                    placeholder="Describe what you need (e.g., 'Need a laptop for remote work')"
                                    rows={3}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Priority</Label>
                                <Select
                                    value={requestData.priority}
                                    onValueChange={(value) => setRequestData({ ...requestData, priority: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {REQUEST_PRIORITIES.map((p) => (
                                            <SelectItem key={p.value} value={p.value}>
                                                {p.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Justification</Label>
                                <Textarea
                                    value={requestData.justification}
                                    onChange={(e) => setRequestData({ ...requestData, justification: e.target.value })}
                                    placeholder="Why do you need this asset?"
                                    rows={2}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowRequestModal(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={submitting || !requestData.category}>
                                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Submit Request
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Request Detail Modal */}
            <Dialog open={showRequestDetailModal} onOpenChange={setShowRequestDetailModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <div className="flex justify-between items-center pr-8">
                            <DialogTitle>Asset Request Details</DialogTitle>
                            <Button variant="outline" size="sm" onClick={() => downloadRequestPDF(selectedRequest)}>
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF
                            </Button>
                        </div>
                    </DialogHeader>
                    {selectedRequest && (
                        <div className="space-y-6 py-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground uppercase">Category</Label>
                                    <div>
                                        <Badge variant="secondary">
                                            {getCategoryLabel(selectedRequest.category)}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="space-y-1 text-right">
                                    <Label className="text-xs text-muted-foreground uppercase">Requested On</Label>
                                    <p className="font-medium text-sm">{new Date(selectedRequest.created_at).toLocaleDateString()}</p>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground uppercase">Priority</Label>
                                    <div>
                                        {(() => {
                                            const colors = { LOW: 'bg-gray-500', NORMAL: 'bg-blue-500', HIGH: 'bg-orange-500', URGENT: 'bg-red-500' };
                                            return (
                                                <Badge variant="outline" className="flex items-center gap-1 w-fit">
                                                    <span className={`w-2 h-2 rounded-full ${colors[selectedRequest.priority] || 'bg-gray-400'}`}></span>
                                                    {selectedRequest.priority || 'NORMAL'}
                                                </Badge>
                                            );
                                        })()}
                                    </div>
                                </div>
                                <div className="space-y-1 text-right">
                                    <Label className="text-xs text-muted-foreground uppercase">Status</Label>
                                    <div className="flex justify-end">
                                        {(() => {
                                            const colors = { PENDING: 'bg-yellow-500', APPROVED: 'bg-green-500', REJECTED: 'bg-red-500', FULFILLED: 'bg-blue-500' };
                                            return (
                                                <Badge variant="outline" className="flex items-center gap-1 w-fit">
                                                    <span className={`w-2 h-2 rounded-full ${colors[selectedRequest.status] || 'bg-gray-400'}`}></span>
                                                    {selectedRequest.status}
                                                </Badge>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground uppercase">Description</Label>
                                <div className="p-3 bg-muted rounded-md text-sm whitespace-pre-wrap">
                                    {selectedRequest.description}
                                </div>
                            </div>

                            {selectedRequest.justification && (
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase">Justification</Label>
                                    <div className="p-3 bg-muted/50 border border-border rounded-md text-sm italic">
                                        "{selectedRequest.justification}"
                                    </div>
                                </div>
                            )}

                            {selectedRequest.admin_remarks && (
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase">Admin Remarks</Label>
                                    <div className="p-3 bg-blue-50 border border-blue-100 rounded-md text-sm">
                                        {selectedRequest.admin_remarks}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default MyAssets;
